import {
  createBeeswaxAccount,
  deleteBeeswaxPreset,
  getBeeswaxAccounts,
  getBeeswaxAdPosition,
  getBeeswaxAdvertisers,
  getBeeswaxDevices,
  getBeeswaxEnvironmentTypes,
  getBeeswaxInventorySources,
  getBeeswaxLineItem,
  getBeeswaxLineItemAudience,
  getBeeswaxLineItemsByIds,
  getBeeswaxPresets,
  getBeeswaxPresetsById,
  getPickablePresets,
  getPickableBidModifiers,
  getPickableDeliveryModifiers,
  getCities,
  getCountries,
  getRegions,
} from "@/apis/v1/beeswax";
import { useMutation, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { DATE_FORMAT_ONE } from "@/constants";
import type { BeeswaxApiResponse } from "../LineItem/CreateOrEdit/Display/display.lineitem.interface";
import { BeeswaxLineItemAudiences } from "../Audience/audience.interface";

export const BEESWAX_ACCOUNTS_LIST_KEY = ["beeswax", "accounts"];
export const BEESWAX_ADVERTISERS_LIST_KEY = ["beeswax", "advertisers"];
export const COUNTRIES_KEY = ["beeswax", "countries"];
export const REGIONS_KEY = (search?: string) => ["beeswax", "regions", search];
export const REGIONS_RESOLVE_KEY = (keys?: string) => [
  "beeswax",
  "regions",
  "resolve",
  keys,
];
export const CITIES_KEY = (search?: string) => ["beeswax", "cities", search];
export const CITIES_RESOLVE_KEY = (geonames?: string) => [
  "beeswax",
  "cities",
  "resolve",
  geonames,
];
export const PRESETS_KEY = (
  type?: "campaign" | "line_item" | "targeting_expression",
  page?: number,
  size?: number,
  advertiserCompanyId?: number | null,
  search?: string
) => ["beeswax", "presets", advertiserCompanyId, type, page, size, search];
export const PRESETS_ID_KEY = (id: string) => ["beeswax", "presets", id];
export const PICKABLE_PRESETS_KEY = (
  type?: "campaign" | "line_item" | "targeting_expression",
  advertiserCompanyId?: number | null
) => ["beeswax", "presets", "pickable", type, advertiserCompanyId];

export const PICKABLE_BID_MODIFIERS_KEY = (
  advertiserCompanyId?: number | null
) => ["beeswax", "bidModifiers", "pickable", advertiserCompanyId];
export const PICKABLE_DELIVERY_MODIFIERS_KEY = (
  advertiserCompanyId?: number | null
) => ["beeswax", "deliveryModifiers", "pickable", advertiserCompanyId];

export const BEESWAX_INVENTORY_SOURCES = ["beeswax", "inventory-sources"];
export const BEESWAX_AD_POSITION = ["beeswax", "ad-position"];
export const BEESWAX_ENVIRONMENT_TYPES = ["beeswax", "environment-types"];
export const BEESWAX_DEVICES = ["beeswax", "devices"];

export function useBeeswaxAccountsList() {
  return useQuery(BEESWAX_ACCOUNTS_LIST_KEY, getBeeswaxAccounts);
}

export function useBeeswaxAdvertisersList(onSuccess?: () => void) {
  return useQuery(BEESWAX_ADVERTISERS_LIST_KEY, getBeeswaxAdvertisers, {
    onSuccess,
  });
}

export function useCreateBeeswaxAccount() {
  return useMutation(createBeeswaxAccount);
}

export function useBeeswaxLineItem({
  id,
  enabled = true,
  onSuccess,
  select,
  options = {},
}: {
  id: any;
  enabled?: boolean;
  onSuccess?: (data: any) => void;
  select?: (data: any) => any;
  options?: Record<any, any>;
}) {
  return useQuery<BeeswaxApiResponse>(["beeswax", "lineItem", id], getBeeswaxLineItem, {
    enabled,
    onSuccess,
    select,
    meta: {
      id,
    },
    ...options,
  });
}

// Formatted display shape a list-table row consumes, keyed by line-item id.
export interface DisplayLineItemBeeswaxRow {
  status: string;
  startDate: string;
  endDate: string;
  budget: number | string;
  spend: number | string;
  spendToday: number;
  spend7Days: number;
  spend30Days: number;
  ctr30Days: number;
  osi: string;
}

// Backend returns raw-ish values keyed by id; format status/dates ONCE here so both
// list tables share identical rendering. A missing id (no Beeswax record) is simply
// absent — the row defaults it to N/A.
function formatBeeswaxByIds(
  data: Record<string, any>
): Record<string, DisplayLineItemBeeswaxRow> {
  const out: Record<string, DisplayLineItemBeeswaxRow> = {};
  Object.entries(data ?? {}).forEach(([id, li]: [string, any]) => {
    out[id] = {
      status: li.active ? "Active" : "Not Active",
      startDate: li.startDate ? dayjs(li.startDate).format(DATE_FORMAT_ONE) : "—",
      endDate: li.endDate
        ? dayjs(li.endDate).format(DATE_FORMAT_ONE)
        : "Continuous",
      budget: li.budget ?? "—",
      spend: li.spend ?? 0,
      spendToday: li.spendToday ?? 0,
      spend7Days: li.spend7Days ?? 0,
      spend30Days: li.spend30Days ?? 0,
      ctr30Days: li.ctr30Days ?? 0,
      osi: li.osi ?? "N/A",
    };
  });
  return out;
}

// Batch-fetch the Beeswax fields for a page of DISPLAY line items in ONE call
// (replaces the per-row useBeeswaxLineItem + per-campaign metadata calls on the list
// tables). Keyed on the sorted id set so paging caches each page separately;
// keepPreviousData avoids a table flash while paging.
/** Stable empty map — see the note where it is returned. */
const EMPTY_BEESWAX_BY_ID: Record<string, DisplayLineItemBeeswaxRow> = {};

export function useBeeswaxLineItemsByIds(
  ids: Array<number | string>,
  enabled = true
) {
  const sortedIds = [...(ids ?? [])]
    .map((id) => Number(id))
    .filter((id) => Number.isInteger(id) && id > 0)
    .sort((a, b) => a - b);
  const idsParam = sortedIds.join(",");
  const query = useQuery(
    ["beeswax", "lineItems", "byIds", idsParam],
    getBeeswaxLineItemsByIds,
    {
      enabled: enabled && sortedIds.length > 0,
      keepPreviousData: true,
      // Beeswax spend/status is slow-moving; a small staleTime stops the global
      // refetchOnMount default from firing a background refetch on every remount
      // (which — with the isFetching gate below — would flash skeletons over cached
      // rows, especially the in-campaign table's row-level loader).
      staleTime: 60_000,
      meta: { queryParams: { ids: idsParam } },
      select: formatBeeswaxByIds,
    }
  );
  return {
    // A module-level constant, not a fresh `{}`. Both line-item lists now build
    // their header arrays from this map inside a `useMemo` keyed on it, and an
    // object literal here is a new identity every render — so the memo never
    // held, the headers were rebuilt, and CustomTable's `sortHeader`/`sortedRows`
    // memos invalidated with them, re-sorting the page and re-rendering every row
    // on each parent render. That happens whenever the query has no data: while
    // the batch is in flight, and permanently on a page with no display rows,
    // since the query is disabled when there are no ids.
    byId: (query.data ?? EMPTY_BEESWAX_BY_ID) as Record<
      string,
      DisplayLineItemBeeswaxRow
    >,
    // Gate on isFetching (not isLoading): with keepPreviousData, isLoading is false on a
    // page-change key change, so isLoading would hide the in-flight state and flash the
    // stale/absent map. isFetching is true exactly while a batch loads, and false for a
    // disabled (no-ids) query.
    isLoading: query.isFetching,
    isFetching: query.isFetching,
  };
}

export function useBeeswaxLineItemAudience({
  id,
  enabled = true,
  onSuccess,
}: {
  id: any;
  enabled?: boolean;
  onSuccess?: (data: any) => void;
}) {
  return useQuery<BeeswaxLineItemAudiences>(
    ["beeswax", "lineItem", id, "audience"],
    getBeeswaxLineItemAudience,
    {
      enabled,
      onSuccess,
      meta: {
        id,
      },
    }
  );
}
export function useCountries({
  enabled = true,
  queryParams,
}: {
  enabled?: boolean;
  queryParams?: {
    name: string;
  };
}) {
  return useQuery(COUNTRIES_KEY, getCountries, {
    enabled,
    meta: {
      queryParams,
    },
    staleTime: Infinity,
    // Backend currently returns every country twice (seeded a second time on
    // 2024-01-09). Dedupe by countryCode2 until that's fixed server-side.
    select: (data: any) => {
      if (!Array.isArray(data)) return data;
      const seen = new Set<string>();
      return data.filter((country: any) => {
        const key = country?.countryCode2;
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    },
  });
}

// Beeswax /ref/regions returns { results: [{ region_code, key, region_name,
// country_code_2, ... }] }. Targeting value = `key` (e.g. "USA/TX").
function mapRegionOption(item: any) {
  return {
    label: item?.country_code_2
      ? `${item.region_name} (${item.country_code_2})`
      : item?.region_name,
    value: item?.key,
  };
}
// Beeswax /ref/cities returns { results: [{ id, name, region_code,
// region_name, country_code_2, geoname, ... }] }. Targeting value = `geoname`.
function mapCityOption(item: any) {
  const parts = [item?.name, item?.region_code, item?.country_code_2].filter(
    Boolean
  );
  return {
    label: parts.join(", "),
    value: item?.geoname,
  };
}

// Type-ahead search for regions (states). Enabled only when a search term is
// present, since the ref endpoint is search-driven.
export function useRegionSearch(search: string, enabled = true) {
  return useQuery(REGIONS_KEY(search), getRegions, {
    enabled: enabled && Boolean(search),
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
    meta: { queryParams: { search } },
    select: (data: any) => (data?.results ?? []).map(mapRegionOption),
  });
}

// Type-ahead search for cities.
export function useCitySearch(search: string, enabled = true) {
  return useQuery(CITIES_KEY(search), getCities, {
    enabled: enabled && Boolean(search),
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
    meta: { queryParams: { search } },
    select: (data: any) => (data?.results ?? []).map(mapCityOption),
  });
}

// Resolve already-selected region keys back to {label, value} for edit prefill.
// page_size is set to the number of requested keys so every selected value is
// returned in a single page (the ref endpoint otherwise defaults to ~50 rows).
export function useResolveRegions(keys: string[], enabled = true) {
  const list = keys ?? [];
  const joined = list.join(",");
  return useQuery(REGIONS_RESOLVE_KEY(joined), getRegions, {
    enabled: enabled && Boolean(joined),
    staleTime: Infinity,
    meta: { queryParams: { key__in: joined, page_size: list.length } },
    select: (data: any) => (data?.results ?? []).map(mapRegionOption),
  });
}

// Resolve already-selected city geonames back to {label, value} for edit prefill.
export function useResolveCities(geonames: string[], enabled = true) {
  const list = geonames ?? [];
  const joined = list.join(",");
  return useQuery(CITIES_RESOLVE_KEY(joined), getCities, {
    enabled: enabled && Boolean(joined),
    staleTime: Infinity,
    meta: { queryParams: { geoname__in: joined, page_size: list.length } },
    select: (data: any) => (data?.results ?? []).map(mapCityOption),
  });
}

export function useBeeswaxPresets<TDATA>({
  enabled = true,
  queryParams,
  onSuccess,
}: {
  enabled?: boolean;
  queryParams?: {
    type: "campaign" | "line_item" | "targeting_expression";
    page?: number;
    size?: number;
    // A Contanuity *company* id (agency-only filter). The backend translates it to
    // the Beeswax advertiser_id; advertiser sub-companies are force-scoped and ignore it.
    advertiserCompanyId?: number | null;
    search?: string;
  };
  onSuccess?: (data: any) => void;
}) {
  return useQuery<TDATA>(
    PRESETS_KEY(
      queryParams?.type,
      queryParams?.page,
      queryParams?.size,
      queryParams?.advertiserCompanyId,
      queryParams?.search
    ),
    getBeeswaxPresets,
    {
      enabled,
      meta: {
        queryParams,
      },
      onSuccess,
    }
  );
}
export function useBeeswaxPresetById<TDATA>({
  enabled = true,
  queryParams,
  onSuccess,
  id,
}: {
  enabled?: boolean;
  queryParams?: {
    favorite?: boolean;
  };
  id: string;
  onSuccess?: (data: any) => void;
}) {
  return useQuery<TDATA>(PRESETS_ID_KEY(id), getBeeswaxPresetsById, {
    enabled,
    meta: {
      id,
      queryParams,
    },
    onSuccess,
  });
}

// Presets available to apply on a line item — advertiser-scoped + globals, `content`
// inline. advertiserCompanyId is a Contanuity company id (the line item's
// campaign.company.id); the backend translates it to the Beeswax advertiser id.
export function usePickablePresets<TDATA>({
  enabled = true,
  queryParams,
}: {
  enabled?: boolean;
  queryParams?: {
    type: "campaign" | "line_item" | "targeting_expression";
    advertiserCompanyId?: number | null;
  };
}) {
  return useQuery<TDATA>(
    PICKABLE_PRESETS_KEY(queryParams?.type, queryParams?.advertiserCompanyId),
    getPickablePresets,
    {
      enabled,
      meta: { queryParams },
    }
  );
}

// Bid modifiers available to attach on a line item — advertiser-scoped + globals,
// `terms` inline. advertiserCompanyId is a Contanuity company id (the line item's
// campaign.company.id); the backend translates it to the Beeswax advertiser id.
export function usePickableBidModifiers<TDATA>({
  enabled = true,
  advertiserCompanyId,
}: {
  enabled?: boolean;
  advertiserCompanyId?: number | null;
}) {
  return useQuery<TDATA>(
    PICKABLE_BID_MODIFIERS_KEY(advertiserCompanyId),
    getPickableBidModifiers,
    {
      enabled,
      // Single unpaginated page: the picker filters client-side and the line-item
      // tab resolves its attached modifier out of these results, so a modifier
      // falling off page 1 would render as "attached with 0 terms".
      meta: { queryParams: { advertiserCompanyId, page_size: 500 } },
    }
  );
}

export function usePickableDeliveryModifiers<TDATA>({
  enabled = true,
  advertiserCompanyId,
}: {
  enabled?: boolean;
  advertiserCompanyId?: number | null;
}) {
  return useQuery<TDATA>(
    PICKABLE_DELIVERY_MODIFIERS_KEY(advertiserCompanyId),
    getPickableDeliveryModifiers,
    {
      enabled,
      // Single unpaginated page, same as the bid picker: the Modifiers tab
      // resolves its attached modifier out of these results, so one falling off
      // page 1 would render as "attached with 0 terms".
      meta: { queryParams: { advertiserCompanyId, page_size: 500 } },
    }
  );
}

export function useBeeswaxInventorySources(args?: {
  queryParams?: {
    id?: number;
    name?: string;
    key?: string;
    category?: string;
    viewable?: boolean;
    page_size?: number;
  };
  options?: Record<any, any>;
}) {
  const { queryParams = {}, options = {} } = args ?? {};
  return useQuery(BEESWAX_INVENTORY_SOURCES, getBeeswaxInventorySources, {
    meta: {
      queryParams: { ...queryParams },
    },
    ...options,
    staleTime: Infinity,
  });
}

export function useBeeswaxAdPosition(args?: {
  queryParams?: {
    id?: number;
    name?: string;
    open_rtb_code?: string;
  };
  options?: Record<any, any>;
}) {
  const { queryParams = {}, options = {} } = args ?? {};
  return useQuery(BEESWAX_AD_POSITION, getBeeswaxAdPosition, {
    meta: {
      queryParams: { ...queryParams },
    },
    ...options,
    staleTime: Infinity,
  });
}

export function useBeeswaxEnvironmentTypes(args?: {
  queryParams?: {
    id?: number;
    name?: string;
  };
  options?: Record<any, any>;
}) {
  const { queryParams = {}, options = {} } = args ?? {};
  return useQuery(BEESWAX_ENVIRONMENT_TYPES, getBeeswaxEnvironmentTypes, {
    meta: {
      queryParams: { ...queryParams },
    },
    ...options,
    staleTime: Infinity,
  });
}

export function useBeeswaxDevices(args?: {
  queryParams?: {
    id?: number;
    name?: string;
  };
  options?: Record<any, any>;
}) {
  const { queryParams = {}, options = {} } = args ?? {};
  return useQuery(BEESWAX_DEVICES, getBeeswaxDevices, {
    meta: {
      queryParams: { ...queryParams },
    },
    ...options,
    staleTime: Infinity,
  });
}

export function useBeeswaxPresetDelete(args?: { options?: Record<any, any> }) {
  return useMutation(deleteBeeswaxPreset, args?.options);
}
